import React, { useState, useEffect } from 'react';
import ConfirmModal from '../ConfirmModal';
import FileUpload from '../FileUpload';

export const PageEditor = ({ 
  page, 
  index, 
  onUpdate, 
  onDelete, 
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  checkpoints = [], 
  lessonData, 
  moduleId 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderEditor = () => {
    switch (page.type) {
      case 'content':
        return <ContentPageEditor page={page} onUpdate={onUpdate} moduleId={moduleId} />;
      case 'virtual-class':
      case 'virtual-class-see':
      case 'virtual-class-hear':
      case 'virtual-class-do':
        return <VirtualClassPageEditor page={page} onUpdate={onUpdate} moduleId={moduleId} />;
      case 'interactive-grid':
        return <InteractiveGridPageEditor page={page} onUpdate={onUpdate} moduleId={moduleId} />;
      case 'image':
        return <ImagePageEditor page={page} onUpdate={onUpdate} moduleId={moduleId} />;
      case 'checkpoint-directions':
        return <CheckpointDirectionsEditor page={page} onUpdate={onUpdate} checkpoints={checkpoints} />;
      case 'quiz-question':
      case 'exam-question':
        return <QuizQuestionEditor page={page} onUpdate={onUpdate} checkpoints={checkpoints} lessonData={lessonData} />;
      case 'quiz-results':
      case 'exam-results':
        return <QuizResultsEditor page={page} onUpdate={onUpdate} checkpoints={checkpoints} />;
      case 'exam-intro':
        return <ContentPageEditor page={page} onUpdate={onUpdate} moduleId={moduleId} />;
      default:
        return <div className="text-gray-500">Editor for {page.type} not yet implemented</div>;
    }
  };

  return (
    <div className="border-2 border-primary/20 rounded-lg overflow-hidden shadow-sm bg-gradient-to-br from-white to-gray-50/50">
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3 flex justify-between items-center border-b border-primary/20">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-primary hover:text-primaryDark font-bold text-lg"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          <div className="flex items-center space-x-2">
            <h4 className="font-bold text-gray-800">
              Page {index + 1}: {page.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h4>
            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded">#{page.pageNumber}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 mr-2">Drag to reorder</span>
          {onMoveUp && (
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="px-2 py-1 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              title="Move up"
            >
              ↑
            </button>
          )}
          {onMoveDown && (
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="px-2 py-1 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              title="Move down"
            >
              ↓
            </button>
          )}
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded shadow-sm transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-6 bg-white">
          <PageCommonFields page={page} onUpdate={onUpdate} moduleId={moduleId} />
          <div className="mt-4 pt-4 border-t border-gray-200">
            {renderEditor()}
          </div>
        </div>
      )}
    </div>
  );
};

const PageCommonFields = ({ page, onUpdate, moduleId }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Page ID
          </label>
          <input
            type="text"
            value={page.id || ''}
            onChange={(e) => onUpdate({ id: e.target.value })}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Estimated Time (minutes)
          </label>
          <input
            type="number"
            value={page.estimatedTime || ''}
            onChange={(e) => onUpdate({ estimatedTime: e.target.value ? parseInt(e.target.value) : null })}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
          />
        </div>
      </div>
      <AudioField audio={page.introductionAudio} onUpdate={(audio) => onUpdate({ introductionAudio: audio })} moduleId={moduleId} />
    </div>
  );
};

const AudioField = ({ audio, onUpdate, moduleId }) => {
  const [inputMethod, setInputMethod] = useState('url');
  const defaultAudio = {
    id: `audio-${Date.now()}`,
    url: '',
    label: '',
    autoPlay: false,
    showPlayer: true,
    position: 'top',
  };

  const currentAudio = audio || defaultAudio;

  return (
    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/50">
      <div className="flex justify-between items-center mb-3">
        <label className="text-sm font-semibold text-gray-700">Introduction Audio</label>
        <button
          onClick={() => onUpdate(audio ? null : defaultAudio)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            audio 
              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
              : 'bg-primary text-white hover:bg-primaryDark'
          }`}
        >
          {audio ? 'Remove Audio' : '+ Add Audio'}
        </button>
      </div>
      {audio && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Audio Source</label>
            <div className="flex space-x-2 mb-2">
              <button
                type="button"
                onClick={() => setInputMethod('url')}
                className={`px-3 py-1 text-sm rounded ${
                  inputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enter URL
              </button>
              <button
                type="button"
                onClick={() => setInputMethod('upload')}
                className={`px-3 py-1 text-sm rounded ${
                  inputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload File
              </button>
            </div>
            {inputMethod === 'url' ? (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
                <input
                  type="text"
                  value={currentAudio.url}
                  onChange={(e) => onUpdate({ ...currentAudio, url: e.target.value })}
                  className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                  placeholder="/audio/intro.mp3"
                />
              </div>
            ) : (
              <FileUpload
                moduleId={moduleId}
                onUploadSuccess={(url) => {
                  onUpdate({ ...currentAudio, url });
                  setInputMethod('url');
                }}
                accept="audio/*"
                label="Upload Audio"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input
                type="text"
                value={currentAudio.label}
                onChange={(e) => onUpdate({ ...currentAudio, label: e.target.value })}
                className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
              <select
                value={currentAudio.position}
                onChange={(e) => onUpdate({ ...currentAudio, position: e.target.value })}
                className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              >
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center text-xs font-medium text-gray-700">
              <input
                type="checkbox"
                checked={currentAudio.autoPlay}
                onChange={(e) => onUpdate({ ...currentAudio, autoPlay: e.target.checked })}
                className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              Auto Play
            </label>
            <label className="flex items-center text-xs font-medium text-gray-700">
              <input
                type="checkbox"
                checked={currentAudio.showPlayer}
                onChange={(e) => onUpdate({ ...currentAudio, showPlayer: e.target.checked })}
                className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              Show Player
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const ContentPageEditor = ({ page, onUpdate, moduleId }) => {
  const blocks = page.blocks || [];

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
    };

    if (type === 'text' || type === 'sub-header') {
      newBlock.text = '';
      newBlock.formatting = { bold: [], italic: [] };
      if (type === 'sub-header') {
        newBlock.style = { fontSize: 'xl', fontWeight: 'bold', color: 'gray-800' };
      }
    } else if (type === 'list') {
      newBlock.listType = 'unordered';
      newBlock.items = [];
    } else if (type === 'image') {
      newBlock.imageUrl = '';
      newBlock.altText = '';
      newBlock.caption = '';
      newBlock.downloadable = false;
    } else if (type === 'video') {
      newBlock.videoUrl = '';
      newBlock.thumbnailUrl = '';
      newBlock.title = '';
      newBlock.description = '';
      newBlock.caption = '';
    } else if (type === 'side-by-side') {
      newBlock.layout = 'side-by-side';
      newBlock.gap = 4;
      newBlock.image = {
        id: `block-image-${Date.now()}`,
        type: 'image',
        imageUrl: '',
        altText: '',
        caption: '',
        downloadable: false,
      };
      newBlock.video = {
        id: `block-video-${Date.now()}`,
        type: 'video',
        videoUrl: '',
        thumbnailUrl: '',
        title: '',
        description: '',
        caption: '',
      };
    }

    onUpdate({ blocks: [...blocks, newBlock] });
  };

  const updateBlock = (index, updates) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    onUpdate({ blocks: newBlocks });
  };

  const deleteBlock = (index) => {
    onUpdate({ blocks: blocks.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h5 className="font-bold text-lg text-gray-800">Content Blocks</h5>
        <select
          onChange={(e) => {
            if (e.target.value) {
              addBlock(e.target.value);
              e.target.value = '';
            }
          }}
          className="px-4 py-2 text-sm font-medium border-2 border-primary rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-primary/20"
        >
          <option value="">+ Add Block Type...</option>
          <option value="text">Text</option>
          <option value="sub-header">Sub Header</option>
          <option value="list">List</option>
          <option value="image">Image</option>
          <option value="video">Video</option>
          <option value="side-by-side">Side by Side (Image + Video)</option>
          <option value="directions">Directions</option>
        </select>
      </div>

      {blocks.length === 0 ? (
        <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 font-medium">No blocks yet. Add a block to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {blocks.map((block, index) => (
            <ContentBlockEditor
              key={block.id}
              block={block}
              index={index}
              moduleId={moduleId}
              onUpdate={(updates) => updateBlock(index, updates)}
              onDelete={() => deleteBlock(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentBlockEditor = ({ block, index, onUpdate, onDelete, moduleId }) => {
  return (
    <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 py-2.5 flex justify-between items-center border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
            #{index + 1}
          </span>
          <span className="text-sm font-bold text-gray-700 capitalize">
            {block.type.replace('-', ' ')}
          </span>
        </div>
        <button
          onClick={onDelete}
          className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
        >
          Delete
        </button>
      </div>
      <div className="p-4">
        {block.type === 'text' || block.type === 'sub-header' ? (
          <TextBlockEditor block={block} onUpdate={onUpdate} />
        ) : block.type === 'list' ? (
          <ListBlockEditor block={block} onUpdate={onUpdate} />
        ) : block.type === 'image' ? (
          <ImageBlockEditor block={block} onUpdate={onUpdate} moduleId={moduleId} />
        ) : block.type === 'video' ? (
          <VideoBlockEditor block={block} onUpdate={onUpdate} moduleId={moduleId} />
        ) : block.type === 'side-by-side' ? (
          <SideBySideBlockEditor block={block} onUpdate={onUpdate} moduleId={moduleId} />
        ) : block.type === 'directions' ? (
          <DirectionsBlockEditor block={block} onUpdate={onUpdate} />
        ) : null}
      </div>
    </div>
  );
};

const TextBlockEditor = ({ block, onUpdate }) => {
  const [showFormatting, setShowFormatting] = useState(false);
  const formatting = block.formatting || { bold: [], italic: [] };

  const handleTextChange = (text) => {
    onUpdate({ text });
  };

  const addFormatting = (type, word) => {
    const current = formatting[type] || [];
    if (!current.includes(word)) {
      onUpdate({
        formatting: { ...formatting, [type]: [...current, word] }
      });
    }
  };

  const removeFormatting = (type, word) => {
    const current = formatting[type] || [];
    onUpdate({
      formatting: { ...formatting, [type]: current.filter(w => w !== word) }
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {block.type === 'sub-header' ? 'Sub Header Text' : 'Text Content'}
        </label>
        <textarea
          value={block.text || ''}
          onChange={(e) => handleTextChange(e.target.value)}
          rows={block.type === 'sub-header' ? 2 : 4}
          className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
          placeholder="Enter text..."
        />
      </div>
      
      {block.type === 'sub-header' && block.style && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
            <select
              value={block.style.fontSize || 'xl'}
              onChange={(e) => onUpdate({ style: { ...block.style, fontSize: e.target.value } })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
            >
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
              <option value="xl">Extra Large</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Font Weight</label>
            <select
              value={block.style.fontWeight || 'bold'}
              onChange={(e) => onUpdate({ style: { ...block.style, fontWeight: e.target.value } })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
            <input
              type="text"
              value={block.style.color || 'gray-800'}
              onChange={(e) => onUpdate({ style: { ...block.style, color: e.target.value } })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
              placeholder="gray-800"
            />
          </div>
        </div>
      )}

      {block.type === 'text' && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <button
            onClick={() => setShowFormatting(!showFormatting)}
            className="text-sm font-medium text-gray-700 hover:text-primary"
          >
            {showFormatting ? '▼' : '▶'} Formatting Options
          </button>
          {showFormatting && (
            <div className="mt-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Bold Words (comma-separated)
                </label>
                <input
                  type="text"
                  value={(formatting.bold || []).join(', ')}
                  onChange={(e) => {
                    const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                    onUpdate({ formatting: { ...formatting, bold: words } });
                  }}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded bg-white"
                  placeholder="word1, word2, word3"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Italic Words (comma-separated)
                </label>
                <input
                  type="text"
                  value={(formatting.italic || []).join(', ')}
                  onChange={(e) => {
                    const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                    onUpdate({ formatting: { ...formatting, italic: words } });
                  }}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded bg-white"
                  placeholder="word1, word2, word3"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VideoBlockEditor = ({ block, onUpdate, moduleId }) => {
  const [videoInputMethod, setVideoInputMethod] = useState('url');
  const [thumbnailInputMethod, setThumbnailInputMethod] = useState('url');

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Video Source</label>
        <div className="flex space-x-2 mb-2">
          <button
            type="button"
            onClick={() => setVideoInputMethod('url')}
            className={`px-3 py-1 text-sm rounded ${
              videoInputMethod === 'url'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Enter URL
          </button>
          <button
            type="button"
            onClick={() => setVideoInputMethod('upload')}
            className={`px-3 py-1 text-sm rounded ${
              videoInputMethod === 'upload'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Upload File
          </button>
        </div>
        {videoInputMethod === 'url' ? (
          <input
            type="text"
            value={block.videoUrl || ''}
            onChange={(e) => onUpdate({ videoUrl: e.target.value })}
            className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="https://youtu.be/..."
          />
        ) : (
          <FileUpload
            moduleId={moduleId}
            accept="video/*"
            label=""
            onUploadSuccess={(url) => {
              onUpdate({ videoUrl: url });
              setVideoInputMethod('url');
            }}
          />
        )}
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Thumbnail Source</label>
        <div className="flex space-x-2 mb-2">
          <button
            type="button"
            onClick={() => setThumbnailInputMethod('url')}
            className={`px-3 py-1 text-sm rounded ${
              thumbnailInputMethod === 'url'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Enter URL
          </button>
          <button
            type="button"
            onClick={() => setThumbnailInputMethod('upload')}
            className={`px-3 py-1 text-sm rounded ${
              thumbnailInputMethod === 'upload'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Upload File
          </button>
        </div>
        {thumbnailInputMethod === 'url' ? (
          <input
            type="text"
            value={block.thumbnailUrl || ''}
            onChange={(e) => onUpdate({ thumbnailUrl: e.target.value })}
            className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="https://..."
          />
        ) : (
          <FileUpload
            moduleId={moduleId}
            accept="image/*"
            label=""
            onUploadSuccess={(url) => {
              onUpdate({ thumbnailUrl: url });
              setThumbnailInputMethod('url');
            }}
          />
        )}
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={block.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
        <textarea
          value={block.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          rows={2}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Caption</label>
        <input
          type="text"
          value={block.caption || ''}
          onChange={(e) => onUpdate({ caption: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>
    </div>
  );
};

const SideBySideBlockEditor = ({ block, onUpdate, moduleId }) => {
  const [imageInputMethod, setImageInputMethod] = useState('url');
  const [videoInputMethod, setVideoInputMethod] = useState('url');
  const [thumbnailInputMethod, setThumbnailInputMethod] = useState('url');
  const image = block.image || {
    id: `block-image-${Date.now()}`,
    type: 'image',
    imageUrl: '',
    altText: '',
    caption: '',
    downloadable: false,
  };
  
  const video = block.video || {
    id: `block-video-${Date.now()}`,
    type: 'video',
    videoUrl: '',
    thumbnailUrl: '',
    title: '',
    description: '',
    caption: '',
  };

  if (!image.id) image.id = `block-image-${Date.now()}`;
  if (!image.type) image.type = 'image';
  
  if (!video.id) video.id = `block-video-${Date.now()}`;
  if (!video.type) video.type = 'video';

  const updateImage = (updates) => {
    onUpdate({
      image: {
        ...image,
        id: image.id || `block-image-${Date.now()}`,
        type: 'image',
        ...updates,
      }
    });
  };

  const updateVideo = (updates) => {
    onUpdate({
      video: {
        ...video,
        id: video.id || `block-video-${Date.now()}`,
        type: 'video',
        ...updates,
      }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Gap (spacing between image and video)</label>
        <input
          type="number"
          value={block.gap || 4}
          onChange={(e) => onUpdate({ gap: parseInt(e.target.value) || 4 })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>
      
      <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30">
        <h6 className="font-bold text-gray-800 mb-3 flex items-center">
          <span className="mr-2">📷</span>
          Image (Left Side)
        </h6>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Image Source</label>
            <div className="flex space-x-2 mb-2">
              <button
                type="button"
                onClick={() => setImageInputMethod('url')}
                className={`px-3 py-1 text-xs rounded ${
                  imageInputMethod === 'url'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enter URL
              </button>
              <button
                type="button"
                onClick={() => setImageInputMethod('upload')}
                className={`px-3 py-1 text-xs rounded ${
                  imageInputMethod === 'upload'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload File
              </button>
            </div>
            {imageInputMethod === 'url' ? (
              <input
                type="text"
                value={image.imageUrl || ''}
                onChange={(e) => updateImage({ imageUrl: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              />
            ) : (
              <FileUpload
                moduleId={moduleId}
                accept="image/*"
                label=""
                onUploadSuccess={(url) => {
                  updateImage({ imageUrl: url });
                  setImageInputMethod('url');
                }}
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Alt Text</label>
            <input
              type="text"
              value={image.altText || ''}
              onChange={(e) => updateImage({ altText: e.target.value })}
              placeholder="Descriptive alt text"
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Caption</label>
            <input
              type="text"
              value={image.caption || ''}
              onChange={(e) => updateImage({ caption: e.target.value })}
              placeholder="Image caption"
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            />
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={image.downloadable || false}
              onChange={(e) => updateImage({ downloadable: e.target.checked })}
              className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-700">Downloadable</span>
          </label>
        </div>
      </div>

      <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50/30">
        <h6 className="font-bold text-gray-800 mb-3 flex items-center">
          <span className="mr-2">🎥</span>
          Video (Right Side)
        </h6>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Video Source</label>
            <div className="flex space-x-2 mb-2">
              <button
                type="button"
                onClick={() => setVideoInputMethod('url')}
                className={`px-3 py-1 text-xs rounded ${
                  videoInputMethod === 'url'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enter URL
              </button>
              <button
                type="button"
                onClick={() => setVideoInputMethod('upload')}
                className={`px-3 py-1 text-xs rounded ${
                  videoInputMethod === 'upload'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload File
              </button>
            </div>
            {videoInputMethod === 'url' ? (
              <input
                type="text"
                value={video.videoUrl || ''}
                onChange={(e) => updateVideo({ videoUrl: e.target.value })}
                placeholder="https://youtu.be/... or https://..."
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              />
            ) : (
              <FileUpload
                moduleId={moduleId}
                accept="video/*"
                label=""
                onUploadSuccess={(url) => {
                  updateVideo({ videoUrl: url });
                  setVideoInputMethod('url');
                }}
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Thumbnail Source</label>
            <div className="flex space-x-2 mb-2">
              <button
                type="button"
                onClick={() => setThumbnailInputMethod('url')}
                className={`px-3 py-1 text-xs rounded ${
                  thumbnailInputMethod === 'url'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enter URL
              </button>
              <button
                type="button"
                onClick={() => setThumbnailInputMethod('upload')}
                className={`px-3 py-1 text-xs rounded ${
                  thumbnailInputMethod === 'upload'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload File
              </button>
            </div>
            {thumbnailInputMethod === 'url' ? (
              <input
                type="text"
                value={video.thumbnailUrl || ''}
                onChange={(e) => updateVideo({ thumbnailUrl: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              />
            ) : (
              <FileUpload
                moduleId={moduleId}
                accept="image/*"
                label=""
                onUploadSuccess={(url) => {
                  updateVideo({ thumbnailUrl: url });
                  setThumbnailInputMethod('url');
                }}
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input
              type="text"
              value={video.title || ''}
              onChange={(e) => updateVideo({ title: e.target.value })}
              placeholder="Video title"
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={video.description || ''}
              onChange={(e) => updateVideo({ description: e.target.value })}
              placeholder="Video description"
              rows={2}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Caption</label>
            <input
              type="text"
              value={video.caption || ''}
              onChange={(e) => updateVideo({ caption: e.target.value })}
              placeholder="Video caption"
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ListBlockEditor = ({ block, onUpdate }) => {
  const items = block.items || [];

  const addItem = () => {
    onUpdate({ items: [...items, { text: '', formatting: { bold: [], italic: [] } }] });
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onUpdate({ items: newItems });
  };

  const deleteItem = (index) => {
    onUpdate({ items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <label className="text-xs text-gray-600">List Type:</label>
        <select
          value={block.listType || 'unordered'}
          onChange={(e) => onUpdate({ listType: e.target.value })}
          className="px-2 py-1 text-xs border border-gray-300 rounded"
        >
          <option value="unordered">Unordered</option>
          <option value="ordered">Ordered</option>
        </select>
      </div>
      {items.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded p-2 bg-gray-50">
          <div className="flex items-start space-x-2 mb-2">
            <input
              type="text"
              value={item.text}
              onChange={(e) => updateItem(index, { text: e.target.value })}
              className="flex-1 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              placeholder="List item..."
            />
            <button
              onClick={() => deleteItem(index)}
              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={(item.formatting?.bold || []).join(', ')}
              onChange={(e) => {
                const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                updateItem(index, { formatting: { ...item.formatting, bold: words } });
              }}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
              placeholder="Bold words (comma-separated)"
            />
            <input
              type="text"
              value={(item.formatting?.italic || []).join(', ')}
              onChange={(e) => {
                const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                updateItem(index, { formatting: { ...item.formatting, italic: words } });
              }}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
              placeholder="Italic words (comma-separated)"
            />
          </div>
        </div>
      ))}
      <button
        onClick={addItem}
        className="text-xs text-primary hover:underline"
      >
        + Add Item
      </button>
    </div>
  );
};

const ImageBlockEditor = ({ block, onUpdate, moduleId }) => {
  const [inputMethod, setInputMethod] = useState('url');

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Image Source</label>
        <div className="flex space-x-2 mb-2">
          <button
            type="button"
            onClick={() => setInputMethod('url')}
            className={`px-3 py-1 text-sm rounded ${
              inputMethod === 'url'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Enter URL
          </button>
          <button
            type="button"
            onClick={() => setInputMethod('upload')}
            className={`px-3 py-1 text-sm rounded ${
              inputMethod === 'upload'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Upload File
          </button>
        </div>
        {inputMethod === 'url' ? (
          <input
            type="text"
            value={block.imageUrl || ''}
            onChange={(e) => onUpdate({ imageUrl: e.target.value })}
            className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="https://..."
          />
        ) : (
          <FileUpload
            moduleId={moduleId}
            accept="image/*"
            label=""
            onUploadSuccess={(url) => {
              onUpdate({ imageUrl: url });
              setInputMethod('url');
            }}
          />
        )}
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Alt Text</label>
        <input
          type="text"
          value={block.altText || ''}
          onChange={(e) => onUpdate({ altText: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
          placeholder="Descriptive alt text"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Caption</label>
        <input
          type="text"
          value={block.caption || ''}
          onChange={(e) => onUpdate({ caption: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
          placeholder="Image caption (optional)"
        />
      </div>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={block.downloadable || false}
          onChange={(e) => onUpdate({ downloadable: e.target.checked })}
          className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
        />
        <span className="text-sm font-medium text-gray-700">Downloadable</span>
      </label>
    </div>
  );
};

const DirectionsBlockEditor = ({ block, onUpdate }) => {
  return (
    <div className="space-y-2">
      <textarea
        value={block.text || ''}
        onChange={(e) => onUpdate({ text: e.target.value })}
        rows={3}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        placeholder="Directions text..."
      />
      {block.requirement && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={block.requirement.text || ''}
            onChange={(e) => onUpdate({
              requirement: { ...block.requirement, text: e.target.value }
            })}
            className="px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="Requirement text"
          />
          <input
            type="number"
            value={block.requirement.passingScore || ''}
            onChange={(e) => onUpdate({
              requirement: { ...block.requirement, passingScore: parseInt(e.target.value) || 0 }
            })}
            className="px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="Passing Score"
          />
        </div>
      )}
      <button
        onClick={() => onUpdate({
          requirement: block.requirement ? null : { text: '', passingScore: 80 }
        })}
        className="text-xs text-primary hover:underline"
      >
        {block.requirement ? 'Remove Requirement' : 'Add Requirement'}
      </button>
    </div>
  );
};

const VirtualClassPageEditor = ({ page, onUpdate, moduleId }) => {
  const flashcards = page.flashcards || [];
  const students = page.students || [];
  const isGenericType = page.type === 'virtual-class';
  const step = page.step || null; 
  const steps = page.steps || []; 
  const config = page.config || {};

  const addFlashcard = () => {
    onUpdate({
      flashcards: [...flashcards, {
        id: `flashcard-${Date.now()}`,
        word: '',
        audio: { id: '', url: '' },
      }]
    });
  };

  const updateFlashcard = (index, updates) => {
    const newFlashcards = [...flashcards];
    newFlashcards[index] = { ...newFlashcards[index], ...updates };
    onUpdate({ flashcards: newFlashcards });
  };

  const addStep = (type) => {
    onUpdate({
      steps: [...steps, {
        id: `step-${Date.now()}`,
        type,
        instruction: '',
        recordingTimeLimit: type === 'hear' || type === 'do' ? 5 : null,
      }]
    });
  };

  const updateStep = (index, updates) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], ...updates };
    onUpdate({ steps: newSteps });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={page.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Students Image URL</label>
        <input
          type="text"
          value={page.studentsImage || ''}
          onChange={(e) => onUpdate({ studentsImage: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
          placeholder="/asset/images/home/ai_students.png"
        />
      </div>

      <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50/30">
        <div className="flex justify-between items-center mb-4">
          <h5 className="font-bold text-lg text-gray-800">Flashcards ({flashcards.length})</h5>
          <button
            onClick={addFlashcard}
            className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm"
          >
            + Add Flashcard
          </button>
        </div>
        <div className="space-y-4">
          {flashcards.map((fc, index) => (
            <FlashcardEditor
              key={fc.id}
              flashcard={fc}
              index={index}
              onUpdate={(updates) => updateFlashcard(index, updates)}
              onDelete={() => onUpdate({ flashcards: flashcards.filter((_, i) => i !== index) })}
              moduleId={moduleId}
              pageType={page.type}
            />
          ))}
        </div>
      </div>

      {isGenericType ? (
        <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/30">
          <div className="flex justify-between items-center mb-4">
            <h5 className="font-bold text-lg text-gray-800">Steps ({steps.length})</h5>
            <div className="flex space-x-2">
              <button
                onClick={() => addStep('intro')}
                className="px-3 py-1 text-xs font-medium border-2 border-orange-300 rounded-lg hover:bg-orange-100"
              >
                + Intro
              </button>
              <button
                onClick={() => addStep('see')}
                className="px-3 py-1 text-xs font-medium border-2 border-orange-300 rounded-lg hover:bg-orange-100"
              >
                + See
              </button>
              <button
                onClick={() => addStep('hear')}
                className="px-3 py-1 text-xs font-medium border-2 border-orange-300 rounded-lg hover:bg-orange-100"
              >
                + Hear
              </button>
              <button
                onClick={() => addStep('do')}
                className="px-3 py-1 text-xs font-medium border-2 border-orange-300 rounded-lg hover:bg-orange-100"
              >
                + Do
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <StepEditor
                key={step.id}
                step={step}
                index={index}
                moduleId={moduleId}
                onUpdate={(updates) => updateStep(index, updates)}
                onDelete={() => onUpdate({ steps: steps.filter((_, i) => i !== index) })}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/30">
          <h5 className="font-bold text-lg text-gray-800 mb-4">Step ({step?.type || 'N/A'})</h5>
          {step ? (
            <StepEditor
              step={step}
              index={0}
              onUpdate={(updates) => onUpdate({ step: { ...step, ...updates } })}
              moduleId={moduleId}
              onDelete={() => onUpdate({ step: null })}
            />
          ) : (
            <button
              onClick={() => {
                const stepType = page.type?.replace('virtual-class-', '') || 'see';
                onUpdate({
                  step: {
                    id: `step-${stepType}`,
                    type: stepType,
                    instruction: '',
                    recordingTimeLimit: stepType === 'hear' || stepType === 'do' ? 5 : null,
                  }
                });
              }}
              className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              + Add Step
            </button>
          )}
        </div>
      )}

      {/* Config */}
      <div className="border-2 border-indigo-200 rounded-lg p-4 bg-indigo-50/30">
        <h5 className="font-bold text-lg text-gray-800 mb-4">Configuration</h5>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.showScoring || false}
              onChange={(e) => onUpdate({ config: { ...config, showScoring: e.target.checked } })}
              className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-700">Show Scoring</span>
          </label>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Passing Score</label>
            <input
              type="number"
              value={config.passingScore || ''}
              onChange={(e) => onUpdate({ config: { ...config, passingScore: parseInt(e.target.value) || null } })}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              placeholder="80"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Recording Time Limit (seconds)</label>
            <input
              type="number"
              value={config.recordingTimeLimit || ''}
              onChange={(e) => onUpdate({ config: { ...config, recordingTimeLimit: parseInt(e.target.value) || null } })}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              placeholder="9"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Board Display Mode</label>
            <select
              value={config.boardMode || 'individual'}
              onChange={(e) => onUpdate({ config: { ...config, boardMode: e.target.value } })}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
            >
              <option value="individual">Individual (5 boards)</option>
              <option value="single">Single shared board</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Choose whether students see individual boards or one shared board in DO step</p>
          </div>
          {config.aiStudentBehavior && (
            <div className="col-span-2 border-t border-indigo-200 pt-3 mt-2">
              <h6 className="text-sm font-semibold text-gray-700 mb-2">AI Student Behavior</h6>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.aiStudentBehavior.responsive || false}
                    onChange={(e) => onUpdate({
                      config: {
                        ...config,
                        aiStudentBehavior: { ...config.aiStudentBehavior, responsive: e.target.checked }
                      }
                    })}
                    className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Responsive</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.aiStudentBehavior.confusionOnError || false}
                    onChange={(e) => onUpdate({
                      config: {
                        ...config,
                        aiStudentBehavior: { ...config.aiStudentBehavior, confusionOnError: e.target.checked }
                      }
                    })}
                    className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Confusion On Error</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FlashcardEditor = ({ flashcard, index, onUpdate, onDelete, moduleId, pageType }) => {
  const [showReminder, setShowReminder] = useState(!!flashcard.reminderAudio);
  const [showCustomResponse, setShowCustomResponse] = useState(!!flashcard.customResponse);
  const [mainAudioInputMethod, setMainAudioInputMethod] = useState('url');
  const [hearReminderInputMethod, setHearReminderInputMethod] = useState('url');
  const [doReminderInputMethod, setDoReminderInputMethod] = useState('url');

  return (
    <div className="border-2 border-green-200 rounded-lg p-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-bold text-green-700">Flashcard {index + 1}</span>
        <button
          onClick={onDelete}
          className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
        >
          Delete
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Word *</label>
          <input
            type="text"
            value={flashcard.word || ''}
            onChange={(e) => onUpdate({ word: e.target.value })}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Aa"
          />
        </div>

        <div className="border border-green-200 rounded-lg p-3 bg-green-50/30">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Main Audio</label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Audio ID</label>
              <input
                type="text"
                value={flashcard.audio?.id || ''}
                onChange={(e) => onUpdate({
                  audio: { ...flashcard.audio, id: e.target.value }
                })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                placeholder="audio-aa"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Audio Source</label>
              <div className="flex space-x-1 mb-1">
                <button
                  type="button"
                  onClick={() => setMainAudioInputMethod('url')}
                  className={`px-2 py-1 text-xs rounded ${
                    mainAudioInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => setMainAudioInputMethod('upload')}
                  className={`px-2 py-1 text-xs rounded ${
                    mainAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Upload
                </button>
              </div>
              {mainAudioInputMethod === 'url' ? (
                <input
                  type="text"
                  value={flashcard.audio?.url || ''}
                  onChange={(e) => onUpdate({
                    audio: { ...flashcard.audio, url: e.target.value }
                  })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                  placeholder="https://..."
                />
              ) : (
                <FileUpload
                  moduleId={moduleId}
                  onUploadSuccess={(url) => {
                    onUpdate({
                      audio: { ...flashcard.audio, url }
                    });
                    setMainAudioInputMethod('url');
                  }}
                  accept="audio/*"
                  label=""
                />
              )}
            </div>
          </div>
        </div>

        <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/30">
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-semibold text-gray-700">Reminder Audio</label>
            <button
              onClick={() => {
                if (showReminder) {
                  setShowReminder(false);
                  onUpdate({ reminderAudio: null });
                } else {
                  setShowReminder(true);
                  onUpdate({
                    reminderAudio: {
                      hear: { id: '', url: '' },
                      do: { id: '', url: '' }
                    }
                  });
                }
              }}
              className={`px-2 py-1 text-xs font-medium rounded ${
                showReminder
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {showReminder ? 'Remove' : '+ Add'}
            </button>
          </div>
          {showReminder && flashcard.reminderAudio && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Hear Reminder</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={flashcard.reminderAudio.hear?.id || ''}
                    onChange={(e) => onUpdate({
                      reminderAudio: {
                        ...flashcard.reminderAudio,
                        hear: { ...flashcard.reminderAudio.hear, id: e.target.value }
                      }
                    })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                    placeholder="ID"
                  />
                  <div>
                    <div className="flex space-x-1 mb-1">
                      <button
                        type="button"
                        onClick={() => setHearReminderInputMethod('url')}
                        className={`px-2 py-1 text-xs rounded ${
                          hearReminderInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        URL
                      </button>
                      <button
                        type="button"
                        onClick={() => setHearReminderInputMethod('upload')}
                        className={`px-2 py-1 text-xs rounded ${
                          hearReminderInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Upload
                      </button>
                    </div>
                    {hearReminderInputMethod === 'url' ? (
                      <input
                        type="text"
                        value={flashcard.reminderAudio.hear?.url || ''}
                        onChange={(e) => onUpdate({
                          reminderAudio: {
                            ...flashcard.reminderAudio,
                            hear: { ...flashcard.reminderAudio.hear, url: e.target.value }
                          }
                        })}
                        className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                        placeholder="URL"
                      />
                    ) : (
                      <FileUpload
                        moduleId={moduleId}
                        onUploadSuccess={(url) => {
                          onUpdate({
                            reminderAudio: {
                              ...flashcard.reminderAudio,
                              hear: { ...flashcard.reminderAudio.hear, url }
                            }
                          });
                          setHearReminderInputMethod('url');
                        }}
                        accept="audio/*"
                        label=""
                      />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Do Reminder</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={flashcard.reminderAudio.do?.id || ''}
                    onChange={(e) => onUpdate({
                      reminderAudio: {
                        ...flashcard.reminderAudio,
                        do: { ...flashcard.reminderAudio.do, id: e.target.value }
                      }
                    })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                    placeholder="ID"
                  />
                  <div>
                    <div className="flex space-x-1 mb-1">
                      <button
                        type="button"
                        onClick={() => setDoReminderInputMethod('url')}
                        className={`px-2 py-1 text-xs rounded ${
                          doReminderInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        URL
                      </button>
                      <button
                        type="button"
                        onClick={() => setDoReminderInputMethod('upload')}
                        className={`px-2 py-1 text-xs rounded ${
                          doReminderInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Upload
                      </button>
                    </div>
                    {doReminderInputMethod === 'url' ? (
                      <input
                        type="text"
                        value={flashcard.reminderAudio.do?.url || ''}
                        onChange={(e) => onUpdate({
                          reminderAudio: {
                            ...flashcard.reminderAudio,
                            do: { ...flashcard.reminderAudio.do, url: e.target.value }
                          }
                        })}
                        className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                        placeholder="URL"
                      />
                    ) : (
                      <FileUpload
                        moduleId={moduleId}
                        onUploadSuccess={(url) => {
                          onUpdate({
                            reminderAudio: {
                              ...flashcard.reminderAudio,
                              do: { ...flashcard.reminderAudio.do, url }
                            }
                          });
                          setDoReminderInputMethod('url');
                        }}
                        accept="audio/*"
                        label=""
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border border-yellow-200 rounded-lg p-3 bg-yellow-50/30">
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-semibold text-gray-700">Vowels & Syllable Division</label>
            <button
              onClick={() => {
                if (flashcard.vowels || flashcard.syllableDivision) {
                  onUpdate({ vowels: null, syllableDivision: null });
                } else {
                  onUpdate({ vowels: [], syllableDivision: [] });
                }
              }}
              className={`px-2 py-1 text-xs font-medium rounded ${
                flashcard.vowels || flashcard.syllableDivision
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              {flashcard.vowels || flashcard.syllableDivision ? 'Remove' : '+ Add'}
            </button>
          </div>
          {(flashcard.vowels || flashcard.syllableDivision) && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Vowels (comma-separated indices)</label>
                <input
                  type="text"
                  value={(flashcard.vowels || []).join(', ')}
                  onChange={(e) => {
                    const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                    onUpdate({ vowels: values });
                  }}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                  placeholder="1, 4"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Syllable Division (comma-separated indices)</label>
                <input
                  type="text"
                  value={(flashcard.syllableDivision || []).join(', ')}
                  onChange={(e) => {
                    const values = e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
                    onUpdate({ syllableDivision: values });
                  }}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                  placeholder="3"
                />
              </div>
            </div>
          )}
        </div>

        {/* Custom Response */}
        <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/30">
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-semibold text-gray-700">Custom Response</label>
            <button
              onClick={() => {
                if (showCustomResponse) {
                  setShowCustomResponse(false);
                  onUpdate({ customResponse: null });
                } else {
                  setShowCustomResponse(true);
                  onUpdate({ customResponse: {} });
                }
              }}
              className={`px-2 py-1 text-xs font-medium rounded ${
                showCustomResponse
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              {showCustomResponse ? 'Remove' : '+ Add'}
            </button>
          </div>
          {showCustomResponse && flashcard.customResponse && (
            <div className="space-y-3">
              {/* Determine which response sections to show based on page type */}
              {(!pageType || pageType === 'virtual-class' || pageType === 'virtual-class-hear') && (
                <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/30">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Hear Response</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Correct Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.hear}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            hear: updates
                          }
                        })}
                        type="hear"
                        moduleId={moduleId}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">OnWrong Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.hear?.onWrong}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            hear: {
                              ...flashcard.customResponse.hear,
                              onWrong: updates
                            }
                          }
                        })}
                        type="onWrong"
                        moduleId={moduleId}
                      />
                    </div>
                  </div>
                </div>
              )}
              {(!pageType || pageType === 'virtual-class' || pageType === 'virtual-class-do') && (
                <div className="border border-green-200 rounded-lg p-3 bg-green-50/30">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Do Response</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Correct Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.do}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            do: updates
                          }
                        })}
                        type="do"
                        moduleId={moduleId}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">OnWrong Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.do?.onWrong}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            do: {
                              ...flashcard.customResponse.do,
                              onWrong: updates
                            }
                          }
                        })}
                        type="onWrong"
                        moduleId={moduleId}
                      />
                    </div>
                  </div>
                </div>
              )}
              {(!pageType || pageType === 'virtual-class' || pageType === 'virtual-class-see') && (
                <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/30">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">See Response</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Correct Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.see}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            see: updates
                          }
                        })}
                        type="do"
                        moduleId={moduleId}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">OnWrong Response</label>
                      <CustomResponseEditor
                        response={flashcard.customResponse.see?.onWrong}
                        onUpdate={(updates) => onUpdate({
                          customResponse: {
                            ...flashcard.customResponse,
                            see: {
                              ...flashcard.customResponse.see,
                              onWrong: updates
                            }
                          }
                        })}
                        type="onWrong"
                        moduleId={moduleId}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Response Editor
const CustomResponseEditor = ({ response, onUpdate, type, moduleId }) => {
  const [audioUrlInputMethod, setAudioUrlInputMethod] = useState('url');
  if (!response) {
    return (
      <button
        onClick={() => {
          if (type === 'do' || type === 'onWrong') {
            // For DO and onWrong: simple format with optional text and audio options
            onUpdate({ text: '', audioType: 'default' });
          } else {
            // For HEAR: complex format with scoring
            onUpdate({ scoringMethod: 'audio-comparison', referenceAudio: { id: '', url: '' } });
          }
        }}
        className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
      >
        + Add {type} Response
      </button>
    );
  }

  // For DO and onWrong types, use simplified format: text + audioType
  if (type === 'do' || type === 'onWrong') {
    // Handle legacy formats and convert to new format
    const audioType = response.audioType || 
                      (response.customAudio === 'empty' ? 'empty' : 
                       (response.url || response.audio?.url ? 'url' : 
                        (response.ttsText ? 'tts' : 'default')));
    
    const audioUrl = response.url || response.audio?.url || '';
    const ttsText = response.ttsText || '';
    const text = response.text || '';

    return (
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Text (optional)</label>
          <input
            type="text"
            value={text}
            onChange={(e) => onUpdate({ ...response, text: e.target.value, audioType })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
            placeholder="Leave empty to use flashcard word"
          />
          <p className="text-xs text-gray-500 mt-1">If empty, the flashcard word will be displayed</p>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Audio (optional)</label>
          <select
            value={audioType}
            onChange={(e) => {
              const newAudioType = e.target.value;
              const updates = { ...response, audioType: newAudioType };
              
              // Clean up old format fields
              delete updates.customAudio;
              delete updates.id;
              if (newAudioType !== 'url') {
                delete updates.url;
                delete updates.audio;
              }
              if (newAudioType !== 'tts') {
                delete updates.ttsText;
              }
              
              onUpdate(updates);
            }}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white mb-2"
          >
            <option value="default">Default (use user's recording)</option>
            <option value="tts">TTS (text-to-speech)</option>
            <option value="url">Custom Audio URL</option>
            <option value="empty">Empty (no audio, text only)</option>
          </select>
          
          {audioType === 'tts' && (
            <div className="mt-2">
              <label className="block text-xs text-gray-600 mb-1">TTS Text</label>
              <input
                type="text"
                value={ttsText}
                onChange={(e) => onUpdate({ ...response, ttsText: e.target.value, audioType })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                placeholder="Enter text for TTS"
              />
              <p className="text-xs text-gray-500 mt-1">This text will be converted to speech</p>
            </div>
          )}
          
          {audioType === 'url' && (
            <div className="mt-2">
              <label className="block text-xs text-gray-600 mb-1">Audio Source</label>
              <div className="flex space-x-1 mb-2">
                <button
                  type="button"
                  onClick={() => setAudioUrlInputMethod('url')}
                  className={`px-2 py-1 text-xs rounded ${
                    audioUrlInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Enter URL
                </button>
                <button
                  type="button"
                  onClick={() => setAudioUrlInputMethod('upload')}
                  className={`px-2 py-1 text-xs rounded ${
                    audioUrlInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Upload File
                </button>
              </div>
              {audioUrlInputMethod === 'url' ? (
                <div>
                  <input
                    type="text"
                    value={audioUrl}
                    onChange={(e) => onUpdate({ ...response, url: e.target.value, audioType })}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                    placeholder="https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">URL to the audio file to play</p>
                </div>
              ) : (
                <FileUpload
                  moduleId={moduleId}
                  onUploadSuccess={(url) => {
                    onUpdate({ ...response, url, audioType });
                    setAudioUrlInputMethod('url');
                  }}
                  accept="audio/*"
                  label=""
                />
              )}
            </div>
          )}
          
          {audioType === 'empty' && (
            <p className="text-xs text-gray-500 mt-1">No audio will play - only text will be shown</p>
          )}
        </div>
      </div>
    );
  }

  // Check if it's simple format (just id and url) - for hear responses
  // But skip this for HEAR type - always use full dropdown for HEAR
  const isSimpleFormat = type !== 'hear' && response.id !== undefined && response.url !== undefined && !response.scoringMethod && !response.text && !response.audio && !response.customAudio && !response.audioType;

  // Check if it's text+audio format (for do responses in lesson-13 style)
  // But skip this for HEAR type - always use full dropdown for HEAR
  const isTextAudioFormat = type !== 'hear' && response.text !== undefined && response.audio !== undefined && !response.scoringMethod && !response.audioType;

  if (isSimpleFormat) {
    return (
      <div className="space-y-2">
        {type === 'do' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Custom Response Text (optional)</label>
            <input
              type="text"
              value={response.text || ''}
              onChange={(e) => onUpdate({ ...response, text: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
              placeholder="Enter text to display (optional)"
            />
            <p className="text-xs text-gray-500 mt-1">Text shown to users when they answer correctly</p>
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Custom Audio</label>
          <select
            value={response.customAudio || ''}
            onChange={(e) => {
              const customAudio = e.target.value;
              if (customAudio === 'empty') {
                onUpdate({ ...response, customAudio: 'empty' });
              } else {
                onUpdate({ ...response, customAudio: '' });
              }
            }}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white mb-2"
          >
            <option value="">Custom URL (use fields below)</option>
            <option value="empty">Empty (text only, no audio)</option>
          </select>
          {response.customAudio !== 'empty' && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={response.id || ''}
                onChange={(e) => onUpdate({ ...response, id: e.target.value })}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                placeholder="ID"
              />
              <div>
                <div className="flex space-x-1 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      onUpdate({ ...response, simpleAudioInputMethod: 'url' });
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      (response.simpleAudioInputMethod || 'url') === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onUpdate({ ...response, simpleAudioInputMethod: 'upload' });
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      response.simpleAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Upload
                  </button>
                </div>
                {(response.simpleAudioInputMethod || 'url') === 'url' ? (
                  <input
                    type="text"
                    value={response.url || ''}
                    onChange={(e) => onUpdate({ ...response, url: e.target.value })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                    placeholder="URL"
                  />
                ) : (
                  <FileUpload
                    moduleId={moduleId}
                    onUploadSuccess={(url) => {
                      onUpdate({ ...response, url, simpleAudioInputMethod: 'url' });
                    }}
                    accept="audio/*"
                    label=""
                  />
                )}
              </div>
            </div>
          )}
          {response.customAudio === 'empty' && (
            <p className="text-xs text-gray-500 mt-1">No audio will play - only text will be shown</p>
          )}
        </div>
      </div>
    );
  }

  if (isTextAudioFormat) {
    return (
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Expected Text</label>
          <input
            type="text"
            value={response.text || ''}
            onChange={(e) => onUpdate({ ...response, text: e.target.value })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
            placeholder="perfect"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Custom Audio</label>
          <select
            value={response.customAudio || ''}
            onChange={(e) => {
              const customAudio = e.target.value;
              if (customAudio === 'empty') {
                onUpdate({ ...response, customAudio: 'empty' });
              } else if (customAudio === 'url') {
                onUpdate({ ...response, customAudio: '', audio: response.audio || { id: '', url: '' } });
              } else {
                onUpdate({ ...response, customAudio: '' });
              }
            }}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white mb-2"
          >
            <option value="">None (use default)</option>
            <option value="empty">Empty (text only, no audio)</option>
            <option value="url">Custom URL</option>
          </select>
          {response.customAudio === 'url' && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={response.audio?.id || ''}
                onChange={(e) => onUpdate({
                  ...response,
                  audio: { ...response.audio, id: e.target.value }
                })}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                placeholder="Audio ID"
              />
              <div>
                <div className="flex space-x-1 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      onUpdate({ ...response, textAudioInputMethod: 'url' });
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      (response.textAudioInputMethod || 'url') === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onUpdate({ ...response, textAudioInputMethod: 'upload' });
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      response.textAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Upload
                  </button>
                </div>
                {(response.textAudioInputMethod || 'url') === 'url' ? (
                  <input
                    type="text"
                    value={response.audio?.url || ''}
                    onChange={(e) => onUpdate({
                      ...response,
                      audio: { ...response.audio, url: e.target.value }
                    })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                    placeholder="Audio URL"
                  />
                ) : (
                  <FileUpload
                    moduleId={moduleId}
                    onUploadSuccess={(url) => {
                      onUpdate({
                        ...response,
                        audio: { ...response.audio, url },
                        textAudioInputMethod: 'url'
                      });
                    }}
                    accept="audio/*"
                    label=""
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Complex format with scoring
  // For HEAR type, compute current audioType from response
  const hearAudioType = response.audioType || 
                        (response.customAudio === 'empty' ? 'empty' : 
                         (response.customAudio === 'url' ? 'url' : 
                          (response.ttsText ? 'tts' : 'default')));
  
  return (
    <div className="space-y-2">
      {type === 'do' && (
        <div>
          <label className="block text-xs text-gray-600 mb-1">Custom Response Text (optional)</label>
          <input
            type="text"
            value={response.text || ''}
            onChange={(e) => onUpdate({ ...response, text: e.target.value })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
            placeholder="Enter text to display (optional)"
          />
          <p className="text-xs text-gray-500 mt-1">Text shown to users when they answer correctly</p>
        </div>
      )}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Audio (optional)</label>
        <select
          value={hearAudioType}
          onChange={(e) => {
            const newAudioType = e.target.value;
            const updates = { ...response, audioType: newAudioType };
            
            // Clean up old format fields
            delete updates.customAudio;
            if (newAudioType !== 'url') {
              delete updates.url;
              delete updates.audio;
            }
            if (newAudioType !== 'tts') {
              delete updates.ttsText;
            }
            
            onUpdate(updates);
          }}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white mb-2"
        >
          <option value="default">Default (use user's recording)</option>
          <option value="tts">TTS (text-to-speech)</option>
          <option value="url">Custom Audio URL</option>
          <option value="empty">Empty (no audio, text only)</option>
        </select>
        
        {hearAudioType === 'tts' && (
          <div className="mt-2">
            <label className="block text-xs text-gray-600 mb-1">TTS Text</label>
            <input
              type="text"
              value={response.ttsText || ''}
              onChange={(e) => onUpdate({ ...response, ttsText: e.target.value, audioType: 'tts' })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
              placeholder="Enter text for TTS"
            />
            <p className="text-xs text-gray-500 mt-1">This text will be converted to speech</p>
          </div>
        )}
        
        {hearAudioType === 'url' && (
          <div className="mt-2">
            <label className="block text-xs text-gray-600 mb-1">Audio Source</label>
            <div className="flex space-x-1 mb-2">
              <button
                type="button"
                onClick={() => setAudioUrlInputMethod('url')}
                className={`px-2 py-1 text-xs rounded ${
                  audioUrlInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Enter URL
              </button>
              <button
                type="button"
                onClick={() => setAudioUrlInputMethod('upload')}
                className={`px-2 py-1 text-xs rounded ${
                  audioUrlInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload File
              </button>
            </div>
            {audioUrlInputMethod === 'url' ? (
              <div>
                <input
                  type="text"
                  value={response.url || response.audio?.url || ''}
                  onChange={(e) => onUpdate({ ...response, url: e.target.value, audioType: 'url' })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">URL to the audio file to play</p>
              </div>
            ) : (
              <FileUpload
                moduleId={moduleId}
                onUploadSuccess={(url) => {
                  onUpdate({ ...response, url, audioType: 'url' });
                  setAudioUrlInputMethod('url');
                }}
                accept="audio/*"
                label=""
              />
            )}
          </div>
        )}
        
        {hearAudioType === 'empty' && (
          <p className="text-xs text-gray-500 mt-1">No audio will play - only text will be shown</p>
        )}
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Scoring Method</label>
        <select
          value={response.scoringMethod || 'audio-comparison'}
          onChange={(e) => onUpdate({ ...response, scoringMethod: e.target.value })}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
        >
          <option value="audio-comparison">Audio Comparison</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Reference Audio</label>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input
            type="text"
            value={response.referenceAudio?.id || ''}
            onChange={(e) => onUpdate({
              ...response,
              referenceAudio: { ...response.referenceAudio, id: e.target.value }
            })}
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
            placeholder="ID"
          />
          <div>
            <div className="flex space-x-1 mb-1">
              <button
                type="button"
                onClick={() => {
                  const currentMethod = response.referenceAudioInputMethod || 'url';
                  onUpdate({
                    ...response,
                    referenceAudioInputMethod: 'url'
                  });
                }}
                className={`px-2 py-1 text-xs rounded ${
                  (response.referenceAudioInputMethod || 'url') === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                URL
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdate({
                    ...response,
                    referenceAudioInputMethod: 'upload'
                  });
                }}
                className={`px-2 py-1 text-xs rounded ${
                  response.referenceAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upload
              </button>
            </div>
            {(response.referenceAudioInputMethod || 'url') === 'url' ? (
              <input
                type="text"
                value={response.referenceAudio?.url || ''}
                onChange={(e) => onUpdate({
                  ...response,
                  referenceAudio: { ...response.referenceAudio, url: e.target.value }
                })}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                placeholder="URL"
              />
            ) : (
              <FileUpload
                moduleId={moduleId}
                onUploadSuccess={(url) => {
                  onUpdate({
                    ...response,
                    referenceAudio: { ...response.referenceAudio, url },
                    referenceAudioInputMethod: 'url'
                  });
                }}
                accept="audio/*"
                label=""
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Editor Component
const StepEditor = ({ step, index, onUpdate, onDelete, moduleId }) => {
  const [stepIntroAudioInputMethod, setStepIntroAudioInputMethod] = useState('url');
  const [stepReminderAudioInputMethod, setStepReminderAudioInputMethod] = useState('url');
  const stepIntroAudio = step.introductionAudio || {
    id: '',
    url: '',
    label: '',
    autoPlay: false,
    showPlayer: true
  };

  return (
    <div className="border-2 border-orange-200 rounded-lg p-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-bold text-orange-700">{step.type?.toUpperCase() || 'STEP'} {index + 1}</span>
        <button
          onClick={onDelete}
          className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
        >
          Delete
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Step Type</label>
          <input
            type="text"
            value={step.type || ''}
            onChange={(e) => onUpdate({ type: e.target.value })}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="see, hear, do, intro"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Instruction</label>
          <textarea
            value={step.instruction || ''}
            onChange={(e) => onUpdate({ instruction: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Step instruction..."
          />
        </div>

        {/* Step Introduction Audio */}
        <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/30">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Introduction Audio</label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">ID</label>
              <input
                type="text"
                value={stepIntroAudio.id || ''}
                onChange={(e) => onUpdate({
                  introductionAudio: { ...stepIntroAudio, id: e.target.value }
                })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Audio Source</label>
              <div className="flex space-x-1 mb-1">
                <button
                  type="button"
                  onClick={() => setStepIntroAudioInputMethod('url')}
                  className={`px-2 py-1 text-xs rounded ${
                    stepIntroAudioInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => setStepIntroAudioInputMethod('upload')}
                  className={`px-2 py-1 text-xs rounded ${
                    stepIntroAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Upload
                </button>
              </div>
              {stepIntroAudioInputMethod === 'url' ? (
                <input
                  type="text"
                  value={stepIntroAudio.url || ''}
                  onChange={(e) => onUpdate({
                    introductionAudio: { ...stepIntroAudio, url: e.target.value }
                  })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                />
              ) : (
                <FileUpload
                  moduleId={moduleId}
                  onUploadSuccess={(url) => {
                    onUpdate({
                      introductionAudio: { ...stepIntroAudio, url }
                    });
                    setStepIntroAudioInputMethod('url');
                  }}
                  accept="audio/*"
                  label=""
                />
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Label</label>
              <input
                type="text"
                value={stepIntroAudio.label || ''}
                onChange={(e) => onUpdate({
                  introductionAudio: { ...stepIntroAudio, label: e.target.value }
                })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
              />
            </div>
            <div className="flex items-end space-x-2">
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={stepIntroAudio.autoPlay || false}
                  onChange={(e) => onUpdate({
                    introductionAudio: { ...stepIntroAudio, autoPlay: e.target.checked }
                  })}
                  className="mr-1"
                />
                Auto Play
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={stepIntroAudio.showPlayer !== false}
                  onChange={(e) => onUpdate({
                    introductionAudio: { ...stepIntroAudio, showPlayer: e.target.checked }
                  })}
                  className="mr-1"
                />
                Show Player
              </label>
            </div>
          </div>
        </div>

        {/* Reminder Audio */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-semibold text-gray-700">Reminder Audio (optional)</label>
            <button
              onClick={() => {
                if (step.reminderAudio) {
                  onUpdate({ reminderAudio: null });
                } else {
                  onUpdate({ reminderAudio: { id: '', url: '' } });
                }
              }}
              className={`px-2 py-1 text-xs font-medium rounded ${
                step.reminderAudio
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {step.reminderAudio ? 'Remove' : '+ Add'}
            </button>
          </div>
          {step.reminderAudio && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={step.reminderAudio.id || ''}
                onChange={(e) => onUpdate({
                  reminderAudio: { ...step.reminderAudio, id: e.target.value }
                })}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                placeholder="ID"
              />
              <div>
                <div className="flex space-x-1 mb-1">
                  <button
                    type="button"
                    onClick={() => setStepReminderAudioInputMethod('url')}
                    className={`px-2 py-1 text-xs rounded ${
                      stepReminderAudioInputMethod === 'url' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setStepReminderAudioInputMethod('upload')}
                    className={`px-2 py-1 text-xs rounded ${
                      stepReminderAudioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Upload
                  </button>
                </div>
                {stepReminderAudioInputMethod === 'url' ? (
                  <input
                    type="text"
                    value={step.reminderAudio.url || ''}
                    onChange={(e) => onUpdate({
                      reminderAudio: { ...step.reminderAudio, url: e.target.value }
                    })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white w-full"
                    placeholder="URL"
                  />
                ) : (
                  <FileUpload
                    moduleId={moduleId}
                    onUploadSuccess={(url) => {
                      onUpdate({
                        reminderAudio: { ...step.reminderAudio, url }
                      });
                      setStepReminderAudioInputMethod('url');
                    }}
                    accept="audio/*"
                    label=""
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Recording Time Limit */}
        {(step.type === 'hear' || step.type === 'do') && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Recording Time Limit (seconds)</label>
            <input
              type="number"
              value={step.recordingTimeLimit || ''}
              onChange={(e) => onUpdate({ recordingTimeLimit: parseInt(e.target.value) || null })}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="5"
            />
          </div>
        )}

        {/* Scoring */}
        {step.scoring && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <label className="block text-xs font-semibold text-gray-700 mb-2">Scoring</label>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Method</label>
                <input
                  type="text"
                  value={step.scoring.method || ''}
                  onChange={(e) => onUpdate({
                    scoring: { ...step.scoring, method: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Threshold</label>
                <input
                  type="number"
                  value={step.scoring.threshold || ''}
                  onChange={(e) => onUpdate({
                    scoring: { ...step.scoring, threshold: parseFloat(e.target.value) || null }
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Message</label>
                <input
                  type="text"
                  value={step.scoring.message || ''}
                  onChange={(e) => onUpdate({
                    scoring: { ...step.scoring, message: e.target.value }
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Interactive Grid Page Editor
const InteractiveGridPageEditor = ({ page, onUpdate, moduleId }) => {
  const grid = page.grid || {
    mode: 'familiarization',
    type: 'alphabet',
    layout: '5x5',
    columns: 5,
    rows: 5,
    cells: [],
  };

  const updateGrid = (updates) => {
    onUpdate({ grid: { ...grid, ...updates } });
  };

  const addCell = () => {
    updateGrid({
      cells: [...grid.cells, {
        id: `cell-${Date.now()}`,
        letter: '',
        subLetter: '',
        displayText: '',
        clickable: true,
      }]
    });
  };

  const updateCell = (index, updates) => {
    const newCells = [...grid.cells];
    newCells[index] = { ...newCells[index], ...updates };
    updateGrid({ cells: newCells });
  };

  const config = page.config || {};
  const instructions = page.instructions || { text: '', formatting: { bold: [], italic: [] } };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={page.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Instructions */}
      <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50/30">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Instructions</label>
        <textarea
          value={instructions.text || ''}
          onChange={(e) => onUpdate({
            instructions: { ...instructions, text: e.target.value }
          })}
          rows={3}
          className="w-full px-4 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 mb-2"
          placeholder="Enter instructions for the grid..."
        />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Bold Words (comma-separated)</label>
            <input
              type="text"
              value={(instructions.formatting?.bold || []).join(', ')}
              onChange={(e) => {
                const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                onUpdate({
                  instructions: {
                    ...instructions,
                    formatting: { ...instructions.formatting, bold: words }
                  }
                });
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white"
              placeholder="word1, word2"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Italic Words (comma-separated)</label>
            <input
              type="text"
              value={(instructions.formatting?.italic || []).join(', ')}
              onChange={(e) => {
                const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                onUpdate({
                  instructions: {
                    ...instructions,
                    formatting: { ...instructions.formatting, italic: words }
                  }
                });
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white"
              placeholder="word1, word2"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Mode</label>
          <select
            value={grid.mode}
            onChange={(e) => updateGrid({ mode: e.target.value })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          >
            <option value="familiarization">Familiarization</option>
            <option value="quiz">Quiz</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Type</label>
          <select
            value={grid.type}
            onChange={(e) => updateGrid({ type: e.target.value })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          >
            <option value="alphabet">Alphabet</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Layout</label>
          <input
            type="text"
            value={grid.layout}
            onChange={(e) => updateGrid({ layout: e.target.value })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="5x5"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Columns</label>
          <input
            type="number"
            value={grid.columns}
            onChange={(e) => updateGrid({ columns: parseInt(e.target.value) || 5 })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Rows</label>
          <input
            type="number"
            value={grid.rows}
            onChange={(e) => updateGrid({ rows: parseInt(e.target.value) || 5 })}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <h5 className="font-bold text-lg text-gray-800">Grid Cells ({grid.cells.length})</h5>
          <button
            onClick={addCell}
            className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primaryDark shadow-sm"
          >
            + Add Cell
          </button>
        </div>
        <div className="space-y-3">
          {grid.cells.map((cell, index) => (
            <div key={cell.id} className="border-2 border-gray-200 rounded-lg p-3 bg-white shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-primary">Cell {index + 1}</span>
                <button
                  onClick={() => updateGrid({ cells: grid.cells.filter((_, i) => i !== index) })}
                  className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                >
                  Delete
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Letter</label>
                  <input
                    type="text"
                    value={cell.letter || ''}
                    onChange={(e) => updateCell(index, { letter: e.target.value })}
                    placeholder="A"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sub Letter</label>
                  <input
                    type="text"
                    value={cell.subLetter || ''}
                    onChange={(e) => updateCell(index, { subLetter: e.target.value })}
                    placeholder="a"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Display Text</label>
                  <input
                    type="text"
                    value={cell.displayText || ''}
                    onChange={(e) => updateCell(index, { displayText: e.target.value })}
                    placeholder="Aa"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={cell.clickable !== false}
                      onChange={(e) => updateCell(index, { clickable: e.target.checked })}
                      className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-xs font-medium text-gray-700">Clickable</span>
                  </label>
                </div>
              </div>
              {/* Audio for Cell */}
              <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/30">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-semibold text-gray-700">Cell Audio</label>
                  <button
                    onClick={() => {
                      if (cell.audio) {
                        updateCell(index, { audio: null });
                      } else {
                        updateCell(index, {
                          audio: { id: `audio-${cell.id}`, url: '' }
                        });
                      }
                    }}
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      cell.audio
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-primary text-white hover:bg-primaryDark'
                    }`}
                  >
                    {cell.audio ? 'Remove Audio' : '+ Add Audio'}
                  </button>
                </div>
                {cell.audio && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Audio ID</label>
                      <input
                        type="text"
                        value={cell.audio.id || ''}
                        onChange={(e) => updateCell(index, {
                          audio: { ...cell.audio, id: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                        placeholder="audio-a"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Audio Source</label>
                      <div className="flex space-x-1 mb-1">
                        <button
                          type="button"
                          onClick={() => {
                            const newCells = [...grid.cells];
                            newCells[index] = { ...newCells[index], audioInputMethod: 'url' };
                            onUpdate({ grid: { ...grid, cells: newCells } });
                          }}
                          className={`px-2 py-1 text-xs rounded ${
                            cell.audioInputMethod === 'url' || !cell.audioInputMethod ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          URL
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newCells = [...grid.cells];
                            newCells[index] = { ...newCells[index], audioInputMethod: 'upload' };
                            onUpdate({ grid: { ...grid, cells: newCells } });
                          }}
                          className={`px-2 py-1 text-xs rounded ${
                            cell.audioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Upload
                        </button>
                      </div>
                      {(cell.audioInputMethod === 'url' || !cell.audioInputMethod) ? (
                        <input
                          type="text"
                          value={cell.audio.url || ''}
                          onChange={(e) => updateCell(index, {
                            audio: { ...cell.audio, url: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white"
                          placeholder="/audio/a.mp3"
                        />
                      ) : (
                        <FileUpload
                          moduleId={moduleId}
                          onUploadSuccess={(url) => {
                            const newCells = [...grid.cells];
                            newCells[index] = { 
                              ...newCells[index], 
                              audio: { ...newCells[index].audio, url },
                              audioInputMethod: 'url'
                            };
                            onUpdate({ grid: { ...grid, cells: newCells } });
                          }}
                          accept="audio/*"
                          label=""
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quiz Questions (only for quiz mode) - Using audioList structure */}
      {grid.mode === 'quiz' && (
        <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50/30">
          <div className="flex justify-between items-center mb-4">
            <h5 className="font-bold text-lg text-gray-800">Quiz Questions (AudioList) ({(page.audioList?.files || []).length})</h5>
            <button
              onClick={() => {
                const audioList = page.audioList || { show: true, position: 'right', files: [] };
                onUpdate({
                  audioList: {
                    ...audioList,
                    files: [...audioList.files, {
                      id: `audio-q-${Date.now()}`,
                      url: '',
                      label: '',
                    }]
                  }
                });
              }}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm"
            >
              + Add Question
            </button>
          </div>

          {/* AudioList Settings */}
          <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={page.audioList?.show !== false}
                  onChange={(e) => onUpdate({
                    audioList: { ...page.audioList, show: e.target.checked }
                  })}
                  className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">Show Audio List</span>
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
                <select
                  value={page.audioList?.position || 'right'}
                  onChange={(e) => onUpdate({
                    audioList: { ...page.audioList, position: e.target.value }
                  })}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                >
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {(page.audioList?.files || []).length === 0 ? (
              <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-sm text-gray-500">No questions yet. Add a question to get started.</p>
              </div>
            ) : (
              (page.audioList.files || []).map((audioFile, qIndex) => {
                const correctAnswers = page.correctAnswers || {};
                // Support both old format (single string) and new format (array)
                let correctCellIds = correctAnswers[audioFile.id] || [];
                if (!Array.isArray(correctCellIds)) {
                  // Convert old format to new format
                  correctCellIds = correctCellIds ? [correctCellIds] : [];
                }

                const toggleCorrectCell = (cellId) => {
                  const correctAnswers = page.correctAnswers || {};
                  let currentIds = correctAnswers[audioFile.id] || [];
                  if (!Array.isArray(currentIds)) {
                    currentIds = currentIds ? [currentIds] : [];
                  }
                  
                  const newIds = currentIds.includes(cellId)
                    ? currentIds.filter(id => id !== cellId)
                    : [...currentIds, cellId];
                  
                  onUpdate({
                    correctAnswers: {
                      ...correctAnswers,
                      [audioFile.id]: newIds
                    }
                  });
                };

                return (
                  <div key={audioFile.id || qIndex} className="border-2 border-purple-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-bold text-purple-700">Question {qIndex + 1}</span>
                      <button
                        onClick={() => {
                          const audioList = page.audioList || { files: [] };
                          const newFiles = audioList.files.filter((_, i) => i !== qIndex);
                          const correctAnswers = page.correctAnswers || {};
                          const newCorrectAnswers = { ...correctAnswers };
                          delete newCorrectAnswers[audioFile.id];
                          onUpdate({
                            audioList: { ...audioList, files: newFiles },
                            correctAnswers: newCorrectAnswers
                          });
                        }}
                        className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                      >
                        Delete
                      </button>
                    </div>
                    
                    {/* Question Audio */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-2">Question Audio</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Audio ID *</label>
                          <input
                            type="text"
                            value={audioFile.id || ''}
                            onChange={(e) => {
                              const audioList = page.audioList || { files: [] };
                              const newFiles = [...audioList.files];
                              const oldId = newFiles[qIndex].id;
                              newFiles[qIndex] = { ...newFiles[qIndex], id: e.target.value };
                              
                              // Update correctAnswers key if ID changed
                              const correctAnswers = page.correctAnswers || {};
                              const newCorrectAnswers = { ...correctAnswers };
                              if (oldId && newCorrectAnswers[oldId]) {
                                newCorrectAnswers[e.target.value] = newCorrectAnswers[oldId];
                                delete newCorrectAnswers[oldId];
                              }
                              
                              onUpdate({
                                audioList: { ...audioList, files: newFiles },
                                correctAnswers: newCorrectAnswers
                              });
                            }}
                            className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                            placeholder="audio-es"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Audio Source *</label>
                          <div className="flex space-x-1 mb-2">
                            <button
                              type="button"
                              onClick={() => {
                                const audioList = page.audioList || { files: [] };
                                const newFiles = [...audioList.files];
                                newFiles[qIndex] = { ...newFiles[qIndex], audioInputMethod: 'url' };
                                onUpdate({ audioList: { ...audioList, files: newFiles } });
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                audioFile.audioInputMethod === 'url' || !audioFile.audioInputMethod ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Enter URL
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const audioList = page.audioList || { files: [] };
                                const newFiles = [...audioList.files];
                                newFiles[qIndex] = { ...newFiles[qIndex], audioInputMethod: 'upload' };
                                onUpdate({ audioList: { ...audioList, files: newFiles } });
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                audioFile.audioInputMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Upload File
                            </button>
                          </div>
                          {(audioFile.audioInputMethod === 'url' || !audioFile.audioInputMethod) ? (
                            <input
                              type="text"
                              value={audioFile.url || ''}
                              onChange={(e) => {
                                const audioList = page.audioList || { files: [] };
                                const newFiles = [...audioList.files];
                                newFiles[qIndex] = { ...newFiles[qIndex], url: e.target.value };
                                onUpdate({ audioList: { ...audioList, files: newFiles } });
                              }}
                              className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                              placeholder="https://..."
                            />
                          ) : (
                            <FileUpload
                              moduleId={moduleId}
                              onUploadSuccess={(url) => {
                                const audioList = page.audioList || { files: [] };
                                const newFiles = [...audioList.files];
                                newFiles[qIndex] = { ...newFiles[qIndex], url, audioInputMethod: 'url' };
                                onUpdate({ audioList: { ...audioList, files: newFiles } });
                              }}
                              accept="audio/*"
                              label=""
                            />
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="block text-xs text-gray-600 mb-1">Label (optional)</label>
                        <input
                          type="text"
                          value={audioFile.label || ''}
                          onChange={(e) => {
                            const audioList = page.audioList || { files: [] };
                            const newFiles = [...audioList.files];
                            newFiles[qIndex] = { ...newFiles[qIndex], label: e.target.value };
                            onUpdate({ audioList: { ...audioList, files: newFiles } });
                          }}
                          className="w-full px-3 py-2 text-xs border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                          placeholder="es.mp3"
                        />
                      </div>
                    </div>

                    {/* Correct Answer Cell IDs (Multiple Selection) */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                        Correct Answer Cell IDs * (Select multiple)
                      </label>
                      {grid.cells.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No cells available. Add cells to the grid first.</p>
                      ) : (
                        <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                          <div className="grid grid-cols-2 gap-2">
                            {grid.cells.map(cell => (
                              <label
                                key={cell.id}
                                className="flex items-center space-x-2 p-2 bg-white rounded border border-gray-300 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={correctCellIds.includes(cell.id)}
                                  onChange={() => toggleCorrectCell(cell.id)}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="text-xs text-gray-700">
                                  {cell.id} ({cell.displayText || cell.letter || 'empty'})
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {correctCellIds.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          Selected: {correctCellIds.join(', ')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Maps audio ID "{audioFile.id}" to cell IDs (can select multiple)
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Config Settings */}
      <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/30">
        <h5 className="font-bold text-lg text-gray-800 mb-4">Grid Configuration</h5>
        <div className="grid grid-cols-2 gap-4">
          {grid.mode === 'quiz' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time Limit (seconds)</label>
              <input
                type="number"
                value={config.timeLimit || ''}
                onChange={(e) => onUpdate({
                  config: { ...config, timeLimit: e.target.value ? parseInt(e.target.value) : null }
                })}
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                placeholder="7"
              />
            </div>
          )}
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.showTimer || false}
                onChange={(e) => onUpdate({
                  config: { ...config, showTimer: e.target.checked }
                })}
                className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Show Timer</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.allowRetry || false}
                onChange={(e) => onUpdate({
                  config: { ...config, allowRetry: e.target.checked }
                })}
                className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Allow Retry</span>
            </label>
          </div>
          {grid.mode === 'quiz' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Feedback Type</label>
                <select
                  value={config.feedback || 'immediate'}
                  onChange={(e) => onUpdate({
                    config: { ...config, feedback: e.target.value }
                  })}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                >
                  <option value="immediate">Immediate</option>
                  <option value="delayed">Delayed</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.playSoundBeforeQuestion || false}
                    onChange={(e) => onUpdate({
                      config: { ...config, playSoundBeforeQuestion: e.target.checked }
                    })}
                    className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Play Sound Before Question</span>
                </label>
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cell Font Size</label>
            <input
              type="text"
              value={config.cellFontSize || '1rem'}
              onChange={(e) => onUpdate({
                config: { ...config, cellFontSize: e.target.value }
              })}
              className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
              placeholder="1rem"
            />
            <p className="text-xs text-gray-500 mt-1">Font size for all cell text (e.g., 1rem, 1.25rem, 1.5rem, 18px)</p>
          </div>
          {grid.mode === 'familiarization' && (
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.playSoundOnClick || false}
                onChange={(e) => onUpdate({
                  config: { ...config, playSoundOnClick: e.target.checked }
                })}
                className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Play Sound On Click</span>
            </label>
          )}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.allowMultipleSelections || false}
              onChange={(e) => onUpdate({
                config: { ...config, allowMultipleSelections: e.target.checked }
              })}
              className="mr-2 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-700">Allow Multiple Selections</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Image Page Editor
const ImagePageEditor = ({ page, onUpdate }) => {
  const image = page.image || { id: '', url: '', alt: '', caption: '' };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
        <input
          type="text"
          value={image.url}
          onChange={(e) => onUpdate({ image: { ...image, url: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
        <input
          type="text"
          value={image.alt}
          onChange={(e) => onUpdate({ image: { ...image, alt: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
        <input
          type="text"
          value={image.caption || ''}
          onChange={(e) => onUpdate({ image: { ...image, caption: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
    </div>
  );
};

// Checkpoint Directions Editor
const CheckpointDirectionsEditor = ({ page, onUpdate, checkpoints }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={page.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Checkpoint</label>
        <select
          value={page.checkpointId || ''}
          onChange={(e) => onUpdate({ checkpointId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        >
          <option value="">Select Checkpoint...</option>
          {checkpoints.map((cp) => (
            <option key={cp.id} value={cp.id}>{cp.title}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
        <div className="grid grid-cols-2 gap-4">
          <select
            value={page.icon?.type || 'checkmark'}
            onChange={(e) => onUpdate({ icon: { ...page.icon, type: e.target.value } })}
            className="px-3 py-2 border border-gray-300 rounded"
          >
            <option value="checkmark">Checkmark</option>
            <option value="quiz">Quiz</option>
            <option value="question">Question</option>
          </select>
          <select
            value={page.icon?.color || 'green'}
            onChange={(e) => onUpdate({ icon: { ...page.icon, color: e.target.value } })}
            className="px-3 py-2 border border-gray-300 rounded"
          >
            <option value="green">Green</option>
            <option value="blue">Blue</option>
            <option value="primary">Primary</option>
          </select>
        </div>
      </div>
      {page.blocks && page.blocks.length > 0 && (
        <ContentPageEditor page={page} onUpdate={onUpdate} />
      )}
    </div>
  );
};

// Quiz Question Editor
const QuizQuestionEditor = ({ page, onUpdate, checkpoints, lessonData }) => {
  const [deleteOptionModal, setDeleteOptionModal] = useState({ isOpen: false, optionIndex: null, optionLabel: '' });

  // Normalize option: convert isCorrect to correct, use string "true"/"false"
  const normalizeOption = (opt) => {
    const normalized = { ...opt };
    // Remove isCorrect if it exists
    if ('isCorrect' in normalized) {
      delete normalized.isCorrect;
    }
    // Ensure correct is a string "true" or "false"
    if ('correct' in normalized) {
      const correctValue = normalized.correct;
      normalized.correct = (correctValue === true || correctValue === "true") ? "true" : "false";
    } else {
      normalized.correct = "false";
    }
    return normalized;
  };

  // Ensure question object has all required properties with proper defaults
  const question = {
    id: page.question?.id || `q-${Date.now()}`,
    text: page.question?.text || '',
    type: page.question?.type || 'multiple-choice',
    points: page.question?.points ?? 1,
    ...(page.question || {}),
  };

  // Check if this is an exam question (not a quiz/checkpoint question)
  const isExamQuestion = page.type === 'exam-question';
  
  // Get correct answer from examAnswers (for exams) or checkpointAnswers (for quizzes)
  const getCorrectOptionId = () => {
    if (isExamQuestion) {
      // For exam questions, use examAnswers
      // Note: examAnswers uses page.id as the key (not question.id)
      if (!lessonData?.examAnswers || !page.id) {
        return null;
      }
      return lessonData.examAnswers[page.id] || null;
    } else {
      // For quiz questions, use checkpointAnswers
      if (!lessonData?.checkpointAnswers || !page.checkpointId || !page.id) {
        return null;
      }
      const checkpointAnswers = lessonData.checkpointAnswers[page.checkpointId];
      if (!checkpointAnswers) {
        return null;
      }
      return checkpointAnswers[page.id] || null;
    }
  };

  // Get correct option ID
  const correctOptionId = getCorrectOptionId();

  // Normalize options: ensure they use "correct" (string) instead of "isCorrect"
  // Also sync with examAnswers/checkpointAnswers to mark the correct option
  const rawOptions = Array.isArray(page.options) ? page.options : (page.options ? [page.options] : []);
  
  // Normalize and sync with examAnswers or checkpointAnswers
  const options = rawOptions.map((opt) => {
    const normalized = normalizeOption(opt);
    // Mark as correct if this option's ID matches the one in examAnswers/checkpointAnswers
    if (correctOptionId && normalized.id === correctOptionId) {
      normalized.correct = "true";
    } else if (correctOptionId) {
      // If there's a correct answer, unmark other options
      normalized.correct = "false";
    }
    return normalized;
  });

  // Initialize missing data on first render if needed
  useEffect(() => {
    const updates = {};
    let needsUpdate = false;

    // Check if question needs initialization
    if (!page.question || !page.question.type || page.question.type === '') {
      updates.question = question;
      needsUpdate = true;
    }

    // Check if options needs normalization or sync with checkpointAnswers
    const needsNormalization = rawOptions.some(opt => 
      'isCorrect' in opt || 
      (opt.correct !== "true" && opt.correct !== "false" && opt.correct !== undefined)
    );

    const needsSync = correctOptionId && rawOptions.some((opt, index) => {
      const shouldBeCorrect = opt.id === correctOptionId;
      const isCurrentlyCorrect = opt.correct === true || opt.correct === "true";
      return shouldBeCorrect !== isCurrentlyCorrect;
    });

    if (!Array.isArray(page.options)) {
      updates.options = [];
      needsUpdate = true;
    } else if (needsNormalization || needsSync) {
      const syncedOptions = rawOptions.map((opt) => {
        const normalized = normalizeOption(opt);
        if (correctOptionId && normalized.id === correctOptionId) {
          normalized.correct = "true";
        } else if (correctOptionId) {
          normalized.correct = "false";
        }
        return normalized;
      });
      updates.options = syncedOptions;
      needsUpdate = true;
    }

    // Only update if something needs fixing
    if (needsUpdate) {
      onUpdate(updates);
    }
  }, [page.checkpointId, page.id, correctOptionId, isExamQuestion, lessonData?.examAnswers]); // Re-run when checkpoint, page, or correct answer changes

  const addOption = () => {
    const currentOptions = Array.isArray(page.options) ? page.options : [];
    onUpdate({
      options: [...currentOptions, {
        id: `opt-${Date.now()}`,
        label: String.fromCharCode(65 + currentOptions.length),
        text: '',
        correct: "false", // Use string "false"
      }]
    });
  };

  const updateOption = (index, updates) => {
    const currentOptions = Array.isArray(page.options) ? page.options : [];
    const newOptions = [...currentOptions];
    const updatedOption = { ...newOptions[index], ...updates };
    
    // Normalize correct value to string
    if ('correct' in updates) {
      const correctValue = updates.correct;
      const isCorrect = (correctValue === true || correctValue === "true");
      updatedOption.correct = isCorrect ? "true" : "false";
      // Remove isCorrect if it exists
      if ('isCorrect' in updatedOption) {
        delete updatedOption.isCorrect;
      }
      
      // For exam questions, update examAnswers
      // Note: examAnswers uses page.id as the key (not question.id)
      if (isExamQuestion && page.id) {
        const currentExamAnswers = lessonData?.examAnswers || {};
        let updatedExamAnswers = { ...currentExamAnswers };
        
        if (isCorrect) {
          // Mark this option as correct in examAnswers
          updatedExamAnswers[page.id] = updatedOption.id;
          
          // Unmark all other options in the UI
          newOptions.forEach((opt, i) => {
            if (i !== index) {
              opt.correct = "false";
              if ('isCorrect' in opt) {
                delete opt.isCorrect;
              }
            }
          });
        } else {
          // Unmark - remove from examAnswers if this was the correct one
          if (updatedExamAnswers[page.id] === updatedOption.id) {
            delete updatedExamAnswers[page.id];
          }
        }
        
        newOptions[index] = updatedOption;
        onUpdate({ 
          options: newOptions,
          _updateExamAnswers: updatedExamAnswers
        });
        return;
      }
      
      if (!isExamQuestion && isCorrect) {
        newOptions.forEach((opt, i) => {
          if (i !== index && (opt.correct === "true" || opt.correct === true)) {
            opt.correct = "false";
            if ('isCorrect' in opt) {
              delete opt.isCorrect;
            }
          }
        });
      }
    }
    
    newOptions[index] = updatedOption;
    onUpdate({ options: newOptions });
  };

  const handleDeleteOptionClick = (index) => {
    const option = options[index];
    setDeleteOptionModal({
      isOpen: true,
      optionIndex: index,
      optionLabel: option.label || option.text || `Option ${index + 1}`,
    });
  };

  const confirmDeleteOption = () => {
    if (deleteOptionModal.optionIndex === null) return;
    const currentOptions = Array.isArray(page.options) ? page.options : [];
    const optionToDelete = currentOptions[deleteOptionModal.optionIndex];
    const newOptions = currentOptions.filter((_, i) => i !== deleteOptionModal.optionIndex);
    
    if (isExamQuestion && page.id && optionToDelete?.id) {
      const currentExamAnswers = lessonData?.examAnswers || {};
      const updatedExamAnswers = { ...currentExamAnswers };
      
      if (updatedExamAnswers[page.id] === optionToDelete.id) {
        delete updatedExamAnswers[page.id];
        onUpdate({ 
          options: newOptions,
          _updateExamAnswers: updatedExamAnswers
        });
      } else {
        onUpdate({ options: newOptions });
      }
    } else {
      onUpdate({ options: newOptions });
    }
    
    setDeleteOptionModal({ isOpen: false, optionIndex: null, optionLabel: '' });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Checkpoint</label>
        <select
          value={page.checkpointId || ''}
          onChange={(e) => onUpdate({ checkpointId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        >
          <option value="">Select Checkpoint...</option>
          {checkpoints.map((cp) => (
            <option key={cp.id} value={cp.id}>{cp.title}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Question Number</label>
          <input
            type="number"
            value={page.questionNumber || ''}
            onChange={(e) => onUpdate({ questionNumber: parseInt(e.target.value) || 1 })}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Total Questions</label>
          <input
            type="number"
            value={page.totalQuestions || ''}
            onChange={(e) => onUpdate({ totalQuestions: parseInt(e.target.value) || 1 })}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
        <textarea
          value={question.text}
          onChange={(e) => onUpdate({ question: { ...question, text: e.target.value } })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
        <select
          value={question.type}
          onChange={(e) => onUpdate({ question: { ...question, type: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        >
          <option value="multiple-choice">Multiple Choice</option>
          <option value="true-false">True/False</option>
          <option value="short-answer">Short Answer</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
        <input
          type="number"
          value={question.points}
          onChange={(e) => onUpdate({ question: { ...question, points: parseInt(e.target.value) || 1 } })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      {question.type === 'multiple-choice' && (
        <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
          <div className="flex justify-between items-center mb-3">
            <h5 className="font-semibold text-gray-800">Options ({options.length})</h5>
            <button
              onClick={addOption}
              className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primaryDark shadow-sm"
            >
              + Add Option
            </button>
          </div>
          {options.length === 0 ? (
            <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">No options yet. Click "Add Option" to create options for this question.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {options.map((opt, index) => {
                const isCorrect = (correctOptionId && opt.id === correctOptionId) ||
                  (!correctOptionId && (opt.correct === "true" || opt.correct === true));
                
                return (
                  <div 
                    key={opt.id || index} 
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 shadow-sm transition-all ${
                      isCorrect 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <input
                      type="text"
                      value={opt.label || ''}
                      onChange={(e) => updateOption(index, { label: e.target.value })}
                      className="w-16 px-3 py-2 text-sm font-medium border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                      placeholder="A"
                    />
                    <input
                      type="text"
                      value={opt.text || ''}
                      onChange={(e) => updateOption(index, { text: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                      placeholder="Enter option text..."
                    />
                    <label className={`flex items-center justify-center px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      isCorrect
                        ? 'bg-green-100 border-green-400 hover:bg-green-200'
                        : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                    }`}>
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isCorrect}
                          onChange={(e) => updateOption(index, { correct: e.target.checked ? "true" : "false" })}
                          className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                          style={{
                            accentColor: isCorrect ? '#16a34a' : '#6b7280'
                          }}
                        />
                        {isCorrect && (
                          <svg 
                            className="absolute w-3 h-3 text-white pointer-events-none" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </label>
                    <button
                      onClick={() => handleDeleteOptionClick(index)}
                      className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm"
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Explanation</label>
        <textarea
          value={page.explanation || ''}
          onChange={(e) => onUpdate({ explanation: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>

      <ConfirmModal
        isOpen={deleteOptionModal.isOpen}
        onClose={() => setDeleteOptionModal({ isOpen: false, optionIndex: null, optionLabel: '' })}
        onConfirm={confirmDeleteOption}
        title="Delete Option"
        message={`Are you sure you want to delete option "${deleteOptionModal.optionLabel}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

const QuizResultsEditor = ({ page, onUpdate, checkpoints }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Checkpoint</label>
        <select
          value={page.checkpointId || ''}
          onChange={(e) => onUpdate({ checkpointId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        >
          <option value="">Select Checkpoint...</option>
          {checkpoints.map((cp) => (
            <option key={cp.id} value={cp.id}>{cp.title}</option>
          ))}
        </select>
      </div>
      <p className="text-sm text-gray-500">
        Quiz results page will display automatically based on checkpoint configuration.
      </p>
    </div>
  );
};

